import React, { useState, useRef } from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
} from 'react-native';
import LoadingAnimation from './LoadingAnimation';
import * as ImagePicker from 'expo-image-picker';
import { supabase, STORAGE_BUCKETS, getPublicUrl } from '../services/supabase';
import { getProfilePictureUrl } from '../services/storage';
import Icon from 'react-native-vector-icons/MaterialIcons';

// Conditionally import ImageManipulator only on native platforms
let ImageManipulator;
if (Platform.OS !== 'web') {
  ImageManipulator = require('expo-image-manipulator');
}

/** Sign-up uploads use path like `{userId}/profile-{timestamp}.jpg` (single file, no -medium/-thumbnail). */
const isSignUpProfileStoragePath = (key) =>
  typeof key === 'string' &&
  !key.startsWith('http') &&
  key.includes('/profile-') &&
  /\.jpe?g$/i.test(key);

const resolveDisplayUrl = (key) => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  // Sign-up: exact storage path only (avoid getImageKey suffix variants)
  if (isSignUpProfileStoragePath(key)) {
    return getPublicUrl(STORAGE_BUCKETS.PROFILE_PICTURES, key);
  }
  return getProfilePictureUrl(key, 'large') || getPublicUrl(STORAGE_BUCKETS.PROFILE_PICTURES, key);
};

const ProfilePictureUpload = ({ userId, currentImageUrl, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const errorFallbackStepRef = useRef(0);

  React.useEffect(() => {
    errorFallbackStepRef.current = 0;
    if (currentImageUrl) {
      try {
        const url = resolveDisplayUrl(currentImageUrl);
        setImageUrl(url);
      } catch (error) {
        console.error('Error loading image URL:', error);
        setImageUrl(null);
      }
    } else {
      setImageUrl(null);
    }
  }, [currentImageUrl]);

  const requestPermission = async () => {
    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Sorry, we need camera roll permissions to upload profile pictures.'
        );
        return false;
      }
    }
    return true;
  };

  const pickImage = async () => {
    const hasPermission = await requestPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.6, // Reduced to 60% for smaller file size
        allowsMultipleSelection: false,
      });
      console.log('🔍 ImagePicker result:', JSON.stringify(result, null, 2));

      // Handle both old API (result.uri) and new API (result.assets)
      let imageUri = null;
      if (!result.canceled) {
        if (result.assets && result.assets[0] && result.assets[0].uri) {
          imageUri = result.assets[0].uri;
          console.log('✅ Found URI in result.assets[0].uri:', imageUri.substring(0, 100));
        } else if (result.uri) {
          // Fallback for older API or web
          imageUri = result.uri;
          console.log('✅ Found URI in result.uri:', imageUri.substring(0, 100));
        } else {
          console.warn('⚠️ No URI found in result:', { hasAssets: !!result.assets, hasUri: !!result.uri, keys: Object.keys(result) });
        }
      } else {
        console.log('❌ Image picker was canceled');
      }

      if (imageUri) {
        console.log('🚀 Starting upload with URI:', imageUri.substring(0, 100));
        await uploadImage(imageUri);
      } else {
        console.error('❌ No image URI found to upload');
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image');
    }
  };

  // Helper to resize image using Canvas (web-only)
  const resizeImageWeb = async (uri, width, height) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/jpeg', 0.6);
      };
      img.onerror = reject;
      img.src = uri;
    });
  };

  const uploadImage = async (uri) => {
    try {
      setUploading(true);
      console.log('📤 Starting image upload for user:', userId);

      // Generate base filename (timestamp for uniqueness)
      const timestamp = Date.now();
      const baseFilename = `${userId}-${timestamp}`;

      // Create thumbnails in different sizes
      const sizes = [
        { suffix: '-thumbnail', width: 100, height: 100 },
        { suffix: '-medium', width: 300, height: 300 },
        { suffix: '', width: 800, height: 800 }, // Original (capped at 800x800)
      ];

      console.log('🖼️ Creating image sizes...');

      if (Platform.OS === 'web') {
        // Web: Use Canvas-based resizing
        for (const size of sizes) {
          try {
            const blob = await resizeImageWeb(uri, size.width, size.height);
            const filename = `${baseFilename}${size.suffix}.jpg`;
            console.log(`📁 Uploading ${size.width}x${size.height} as: ${filename} (${Math.round(blob.size / 1024)}KB)`);

            // Upload to Supabase Storage
            const { error } = await supabase.storage
              .from(STORAGE_BUCKETS.PROFILE_PICTURES)
              .upload(filename, blob, {
                contentType: 'image/jpeg',
                upsert: true,
              });

            if (error) {
              console.error(`❌ Upload error for ${filename}:`, error);
              throw error;
            }

            console.log(`✅ Uploaded ${size.width}x${size.height}`);
          } catch (sizeError) {
            console.error(`❌ Failed to upload ${size.width}x${size.height}:`, sizeError);
            throw sizeError;
          }
        }
      } else {
        // Native: Use expo-image-manipulator
        for (const size of sizes) {
          try {
            // Resize image
            const manipResult = await ImageManipulator.manipulateAsync(
              uri,
              [{ resize: { width: size.width, height: size.height } }],
              { compress: 0.6, format: ImageManipulator.SaveFormat.JPEG }
            );

            // Convert to blob
            const response = await fetch(manipResult.uri);
            const blob = await response.blob();

            const filename = `${baseFilename}${size.suffix}.jpg`;
            console.log(`📁 Uploading ${size.width}x${size.height} as: ${filename} (${Math.round(blob.size / 1024)}KB)`);

            // Upload to Supabase Storage
            const { error } = await supabase.storage
              .from(STORAGE_BUCKETS.PROFILE_PICTURES)
              .upload(filename, blob, {
                contentType: 'image/jpeg',
                upsert: true,
              });

            if (error) {
              throw error;
            }

            console.log(`✅ Uploaded ${size.width}x${size.height}`);
          } catch (sizeError) {
            throw sizeError;
          }
        }
      }

      // Store the base filename (without suffix) in the database
      const dbKey = `${baseFilename}.jpg`;
      console.log('💾 Storing base key in database:', dbKey);

      // Get the URL for display (use medium size for profile screen)
      const mediumKey = `${baseFilename}-medium.jpg`;
      const url = getPublicUrl(STORAGE_BUCKETS.PROFILE_PICTURES, mediumKey);
      console.log('✅ Got Supabase URL:', url);

      // Update image URL immediately with the medium size URL
      setImageUrl(url);
      
      // Call callback to let parent component update database
      if (onUploadComplete) {
        await onUploadComplete(dbKey);
      }

      if (typeof window !== 'undefined') {
        window.alert('Success! Profile picture updated.');
      } else {
        Alert.alert('Success', 'Profile picture updated!');
      }
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      // Extract error message properly
      let errorMessage = 'Unknown error';
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.name) {
        errorMessage = error.name;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else {
        try {
          errorMessage = JSON.stringify(error, Object.getOwnPropertyNames(error || {}));
        } catch (e) {
          errorMessage = String(error);
        }
      }
      if (typeof window !== 'undefined') {
        window.alert('Error: Failed to upload image - ' + errorMessage);
      } else {
        Alert.alert('Error', 'Failed to upload image: ' + errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity onPress={pickImage} disabled={uploading}>
        <View style={styles.imageContainer}>
          {imageUrl ? (
            <Image 
              key={imageUrl} 
              source={{ uri: imageUrl }} 
              style={styles.image}
              onError={() => {
                if (!currentImageUrl || currentImageUrl.startsWith('http')) return;
                const step = errorFallbackStepRef.current;
                errorFallbackStepRef.current += 1;
                // Sign-up: only one file at exact path — do NOT try -medium (doesn't exist)
                if (isSignUpProfileStoragePath(currentImageUrl)) {
                  if (step === 0) {
                    const direct = getPublicUrl(STORAGE_BUCKETS.PROFILE_PICTURES, currentImageUrl);
                    if (direct && direct !== imageUrl) setImageUrl(direct);
                  }
                  return;
                }
                // In-app uploads: base key in DB; file is usually -medium.jpg
                if (step === 0 && !currentImageUrl.includes('-medium')) {
                  const baseKey = currentImageUrl.replace(/\.jpe?g$/i, '');
                  const mediumUrl = getPublicUrl(STORAGE_BUCKETS.PROFILE_PICTURES, `${baseKey}-medium.jpg`);
                  setImageUrl(mediumUrl);
                } else if (step === 1) {
                  setImageUrl(resolveDisplayUrl(currentImageUrl));
                }
              }}
            />
          ) : (
            <View style={styles.placeholder}>
              <Icon name="person" size={60} color="#999" />
            </View>
          )}
          {uploading && (
            <View style={styles.loadingOverlay}>
              <LoadingAnimation size={225} />
            </View>
          )}
          <View style={styles.editBadge}>
            <Icon name="camera-alt" size={20} color="white" />
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    marginVertical: 20,
  },
  imageContainer: {
    position: 'relative',
  },
  image: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#f0f0f0',
  },
  placeholder: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: '#ddd',
    borderStyle: 'dashed',
  },
  editBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    backgroundColor: '#94EB98',
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 3,
    borderColor: 'white',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default ProfilePictureUpload;
